import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CatalogService, CatalogWarehouse, CatalogWarehouseBin, InventoryReservation } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-offering-inventory-reservations-panel',
  imports: [
    AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, DialogModule,
    InputTextModule, SelectModule, AinCanAccessDirective
  ],
  template: `
    <ain-info-hint
      [titleKey]="'catalog.offerings.inventory.reservations.hint.title'"
      [descriptionKey]="'catalog.offerings.inventory.reservations.hint.description'" />

    @if (loading()) {
      <ain-state type="loading" [title]="t('catalog.loading')" />
    } @else if (permissionDenied()) {
      <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
    } @else {
      <div class="toolbar">
        <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.outbound.createReservation')" (click)="openCreateDialog()"></button>
        <button pButton type="button" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.pickWaves.createFromSelection')" [disabled]="selectedRows.length === 0" (click)="openPickWaveDialog()"></button>
        <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="reload()"></button>
      </div>

      <p-table [value]="rows()" [(selection)]="selectedRows" dataKey="id">
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 44px"><p-tableHeaderCheckbox /></th>
            <th>{{ t('catalog.outbound.sourceReference') }}</th>
            <th>{{ t('columns.status') }}</th>
            <th>{{ t('catalog.inventory.quantity') }}</th>
            <th>{{ t('catalog.inventory.warehouseId') }}</th>
            <th></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><p-tableCheckbox [value]="row" [disabled]="row.status !== 'Active'" /></td>
            <td>{{ row.sourceType }} — {{ row.sourceReference }}</td>
            <td>{{ row.status }}</td>
            <td>{{ row.quantity }}</td>
            <td>{{ resolveWarehouse(row.warehouseId) }}</td>
            <td class="row-actions">
              @if (row.status === 'Active') {
                <button pButton type="button" size="small" ainCanAccess [ainCanAccessPermission]="'inventory.tasks.execute'" [label]="t('catalog.outbound.createPick')" (click)="createPick(row)"></button>
                <button pButton type="button" size="small" severity="secondary" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('catalog.outbound.release')" (click)="release(row)"></button>
              }
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (message()) {
        <p class="message">{{ message() }}</p>
      }
      @if (error()) {
        <p class="error">{{ error() }}</p>
      }
    }

    <p-dialog [(visible)]="createDialogVisible" [header]="t('catalog.outbound.createReservation')" [modal]="true" [style]="{ width: '480px' }">
      <div class="grid">
        <label>
          <span>{{ t('catalog.inventory.warehouseId') }}</span>
          <p-select [(ngModel)]="form.warehouseId" [options]="warehouseOptions()" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="loadBins()" />
        </label>
        <label>
          <span>{{ t('catalog.inbound.targetBin') }}</span>
          <p-select [(ngModel)]="form.binId" [options]="binOptions()" optionLabel="label" optionValue="value" appendTo="body" />
        </label>
        <label>
          <span>{{ t('catalog.outbound.sourceReference') }}</span>
          <input pInputText [(ngModel)]="form.sourceReference" />
        </label>
        <label>
          <span>{{ t('catalog.inventory.quantity') }}</span>
          <input pInputText type="number" [(ngModel)]="form.quantity" (ngModelChange)="refreshAvailability()" />
        </label>
      </div>
      @if (availability()) {
        <p class="hint">{{ t('catalog.outbound.availability') }}: {{ availability()!.availableQuantity }} / {{ availability()!.onHandQuantity }}</p>
      }
      @if (createError()) {
        <p class="error">{{ createError() }}</p>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
        <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.reservations.create'" [label]="t('common.save')" (click)="create()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog [header]="t('catalog.pickWaves.createTitle')" [visible]="pickWaveDialogOpen()" [modal]="true" [style]="{ width: '28rem' }" (visibleChange)="pickWaveDialogOpen.set($event)">
      <label class="dialog-field">
        <span>{{ t('catalog.pickWaves.referenceNumber') }}</span>
        <input pInputText [(ngModel)]="pickWaveReference" />
      </label>
      <p class="hint">{{ t('catalog.pickWaves.selectedCount') }}: {{ selectedRows.length }}</p>
      <div class="dialog-footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="pickWaveDialogOpen.set(false)"></button>
        <button pButton type="button" [label]="t('catalog.action.create')" (click)="createPickWave()" [disabled]="!pickWaveReference.trim() || pickWaveBusy()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-bottom: 12px; }
    .card h3 { margin: 0 0 10px; font-size: 15px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; }
    .grid label { display: grid; gap: 6px; }
    .grid span { font-size: 12px; color: #475569; font-weight: 600; }
    .hint { margin-top: 8px; color: #475569; font-size: 13px; }
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .row-actions { display: flex; gap: 6px; flex-wrap: wrap; }
    .error { color: #b91c1c; margin-top: 8px; }
    .message { color: #166534; margin-top: 8px; }
    .dialog-field { display: grid; gap: 6px; margin-bottom: 8px; }
    .dialog-footer { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }
  `]
})
export class OfferingInventoryReservationsPanelComponent implements OnInit {
  @Input({ required: true }) offeringId!: string;
  @Output() inventoryChanged = new EventEmitter<void>();

  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  createDialogVisible = false;
  readonly createError = signal('');
  readonly pickWaveDialogOpen = signal(false);
  readonly pickWaveBusy = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly rows = signal<InventoryReservation[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  readonly availability = signal<{ onHandQuantity: number; availableQuantity: number } | null>(null);

  selectedRows: InventoryReservation[] = [];
  pickWaveReference = '';
  form = { warehouseId: '', binId: '', sourceReference: '', quantity: 0 };

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.reload(false)
    );
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.reload();
  }

  warehouseOptions(): Array<{ label: string; value: string }> {
    return this.warehouses().map(w => ({ label: w.code, value: w.id }));
  }

  binOptions(): Array<{ label: string; value: string }> {
    return this.bins().map(b => ({ label: b.code, value: b.id }));
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(item => item.id === id);
    return w ? w.code : id;
  }

  loadBins(): void {
    if (!this.form.warehouseId) return;
    this.service.listWarehouseBins(this.form.warehouseId).subscribe({ next: b => this.bins.set(b) });
    this.refreshAvailability();
  }

  refreshAvailability(): void {
    if (!this.form.warehouseId) {
      this.availability.set(null);
      return;
    }
    this.service.getInventoryAvailability(this.offeringId, this.form.warehouseId, this.form.binId || null)
      .subscribe({ next: a => this.availability.set(a) });
  }

  openCreateDialog(): void {
    this.createError.set('');
    this.form.warehouseId = this.warehouses()[0]?.id ?? '';
    this.form.binId = '';
    this.form.sourceReference = '';
    this.form.quantity = 0;
    if (this.form.warehouseId) {
      this.loadBins();
    }
    this.createDialogVisible = true;
  }

  create(): void {
    this.createError.set('');
    this.error.set('');
    this.service.createInventoryReservation({
      offeringId: this.offeringId,
      warehouseId: this.form.warehouseId,
      binId: this.form.binId || null,
      sourceType: 'Manual',
      sourceReference: this.form.sourceReference,
      quantity: this.form.quantity
    }).subscribe({
      next: () => {
        this.createDialogVisible = false;
        this.message.set(this.t('common.saved'));
        this.reload();
        this.inventoryChanged.emit();
      },
      error: () => this.createError.set(this.t('catalog.errorLoad'))
    });
  }

  createPick(row: InventoryReservation): void {
    this.service.createPickTaskFromReservation(row.id).subscribe({
      next: () => {
        this.message.set(this.t('catalog.outbound.createPick'));
        this.reload();
        this.inventoryChanged.emit();
      }
    });
  }

  release(row: InventoryReservation): void {
    this.service.releaseInventoryReservation(row.id).subscribe({
      next: () => {
        this.reload();
        this.inventoryChanged.emit();
      }
    });
  }

  openPickWaveDialog(): void {
    this.pickWaveReference = `WAVE-${Date.now()}`;
    this.pickWaveDialogOpen.set(true);
  }

  createPickWave(): void {
    const ids = this.selectedRows.map(r => r.id);
    if (!ids.length) return;
    this.pickWaveBusy.set(true);
    this.service.createPickWave(this.pickWaveReference.trim(), ids).subscribe({
      next: () => {
        this.pickWaveBusy.set(false);
        this.pickWaveDialogOpen.set(false);
        this.selectedRows = [];
        this.message.set(this.t('catalog.pickWaves.createdInline'));
        this.reload();
        this.inventoryChanged.emit();
      },
      error: () => this.pickWaveBusy.set(false)
    });
  }

  reload(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.service.listInventoryReservations().subscribe({
      next: list => {
        this.rows.set(list.filter(item => item.offeringId === this.offeringId));
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
    this.refreshAvailability();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
